/**
 * GxTabs: the one tab row (replaces the underline tabs, the segmented tabs and the chip tabs). Underlined text on a row, the active one gold-ruled;
 * arrow keys move between them (the roving model). A tab that is a link-to-a-view passes `onSelect`; the panel it controls is the caller's.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { RovingGroup } from "./roving";
import "./system.css";

export interface GxTab { readonly id: string; readonly label: string; readonly testId?: string; readonly count?: number; readonly disabled?: boolean; /** Before the label (an icon). */ readonly lead?: ReactNode; /** After the label (a state dot); `hidden` text for a screen reader goes in it. */ readonly trail?: ReactNode; readonly title?: string }

export function GxTabs({ tabs, active, onSelect, label = "Views", panelId, testId }: {
  readonly tabs: readonly GxTab[]; readonly active: string; readonly onSelect: (id: string) => void; readonly label?: string; readonly panelId?: string; readonly testId?: string;
}) {
  // A row that scrolls sideways (a phone) brings its active tab into view on mount and on change: its underline is what says where you are. Only the row moves, never the page.
  const on = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const tab = on.current, row = tab?.parentElement;
    if (tab === null || tab === undefined || row === null || row === undefined || row.scrollWidth <= row.clientWidth) return;
    const over = tab.offsetLeft + tab.offsetWidth - (row.scrollLeft + row.clientWidth), under = row.scrollLeft - tab.offsetLeft;
    if (over > 0) row.scrollLeft += over + 16; else if (under > 0) row.scrollLeft -= under + 16;
  }, [active]);
  return (
    <RovingGroup className="gx-tabs" role="tablist" aria-label={label} data-testid={testId}>
      {tabs.map((t) => (
        <button key={t.id} ref={t.id === active ? on : undefined} type="button" role="tab" id={panelId === undefined ? undefined : `${panelId}-tab-${t.id}`} aria-controls={panelId} className="gx-tab" aria-selected={t.id === active} disabled={t.disabled} title={t.title} data-testid={t.testId ?? `tabs.${t.id}`} onClick={() => onSelect(t.id)}>
          {t.lead}{t.label}{t.trail}{t.count === undefined ? null : <span className="gx-tab-n gx-num">{t.count}</span>}
        </button>
      ))}
    </RovingGroup>
  );
}
